/**
 * The home for a user who is starting: the masthead without its workspace
 * switcher, the composer whose send starts a canvas (a chat below lg), and
 * one row of starters, and above it, once a first run has landed, the way
 * back to its canvas. No other recents, no tabs, no view toggle, no New
 * button, no floating chat. Recent work stays reachable from the rail and
 * the workspace pages.
 *
 * The block sits at the page's vertical centre in the production home's
 * column: the composer at its width, and the row inside the same edges with
 * three cards scaled down to fit, its heading and arrows on those edges.
 * A column that holds one card stacks the starters and the page scrolls.
 * A clicked starter, or a desktop send or quick action, hides the block
 * behind the loader until the canvas is ready, and brings it back as it was
 * on failure.
 */

import { cn } from "@/utils";
import { useCallback, useEffect, useRef, useState } from "react";
import { WorkspaceProviders } from "../../components/WorkspaceProviders";
import { HomeGreeting } from "../HomeGreeting";
import { readHomeResetOverride } from "./devOverride";
import { useHomeDispatcher } from "./dispatch";
import { HomeRow, useCardCount } from "./HomeRow";
import "./new-home.css";
import { NewHomeComposer } from "./NewHomeComposer";
import { PickUpRow } from "./PickUpRow";
import { firstRunCanvas, useHomeRecord, type HomeRecord } from "./record";
import { ResetControl } from "./ResetControl";
import { StarterLoading } from "./StarterLoading";
import { useTemplateCopy } from "./useTemplateCopy";

/**
 * The production home's composer column (`HomeSurface`), gutter included:
 * 864px from the md gutter up, three cards of 277px in it.
 */
const COLUMN = "mx-auto w-full max-w-[58rem] px-4 sm:px-6 md:px-8";

export const TOUR_LINES: readonly string[] = [
  "Opening the tour",
  "Almost there",
];
export const SEND_LINES: readonly string[] = ["Creating the canvas"];

/**
 * The walkthrough click, held until the kernel has either navigated or given
 * up. Its start has no promise to await: the loader shows from the click and
 * clears when the pending flag falls with the home still mounted, which is
 * the failure case (the toast is the kernel's).
 */
export function useWalkthroughOpen(walkthrough: HomeRecord["walkthrough"]): {
  start: () => void;
  loading: boolean;
} {
  const [clicked, setClicked] = useState(false);
  const wasPending = useRef(false);
  useEffect(() => {
    if (walkthrough.pending) wasPending.current = true;
    else if (clicked && wasPending.current) {
      wasPending.current = false;
      setClicked(false);
    }
  }, [walkthrough.pending, clicked]);
  const { start: begin } = walkthrough;
  const start = useCallback(() => {
    setClicked(true);
    begin();
  }, [begin]);
  return { start, loading: clicked };
}

export function NewHome() {
  const [showReset] = useState(readHomeResetOverride);
  const { send, busy } = useHomeDispatcher();
  const record = useHomeRecord();
  const templates = useTemplateCopy(record);
  const tour = useWalkthroughOpen(record.walkthrough);
  const [columnRef, cardCount] = useCardCount();
  const stacked = cardCount === 1;
  const pickUp = firstRunCanvas(record.record);
  const loading =
    templates.lines ?? (tour.loading ? TOUR_LINES : busy ? SEND_LINES : null);

  return (
    <WorkspaceProviders>
      <div
        data-testid="new-home"
        className={cn(
          "new-home flex flex-col",
          stacked ? "min-h-full" : "h-full overflow-hidden",
        )}
      >
        {loading && <StarterLoading lines={loading} />}
        <div
          hidden={!!loading}
          // Pinned to the top, so a short window clips the cards below rather than the composer.
          className="flex min-h-0 flex-1 flex-col gap-10 pt-12 pb-10 lg:pt-16"
        >
          <div className={`${COLUMN} flex flex-col gap-4`}>
            <HomeGreeting switcher={false} />
            <NewHomeComposer onSend={send} busy={busy} />
          </div>
          {pickUp && (
            <div className={COLUMN}>
              <PickUpRow canvasId={pickUp} cardCount={cardCount} />
            </div>
          )}
          <div className={COLUMN}>
            <div ref={columnRef} className="min-w-0">
              <HomeRow
                record={record}
                templates={templates}
                onWalkthrough={tour.start}
                cardCount={cardCount}
              />
            </div>
            {showReset && <ResetControl record={record} />}
          </div>
        </div>
      </div>
    </WorkspaceProviders>
  );
}
